import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormFeedback } from '@/components/ui/form-feedback';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { apiClient } from '@/lib/api-client';
import type { paths } from '@/lib/api-types-clinical-study';
import { formatDate } from '@/lib/intl';
import { usePendingWrite } from '@/lib/use-pending-write';
import { useTreatmentDatePoolPicker } from './useTreatmentDatePoolPicker';

type StudySubject =
  paths['/v1/clinical-study/studies/{studyId}/subjects/{subjectId}/treatment-date-reschedule']['post']['responses'][200]['content']['application/json'];

const provisionalRescheduleSchema = z.object({
  treatmentDatePoolEntryId: z.string().uuid('Deve essere un UUID valido'),
});
type ProvisionalRescheduleFormValues = z.infer<typeof provisionalRescheduleSchema>;

const enrolledRescheduleSchema = z.object({
  actualTreatmentDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'La data deve essere nel formato AAAA-MM-GG.'),
});
type EnrolledRescheduleForm = z.infer<typeof enrolledRescheduleSchema>;

/**
 * The provisional-mode reschedule sub-form, extracted into its own component (Resolution R7,
 * logic-simplify-4 fix applied in the 2026-07-27 draft review) so `useTreatmentDatePoolPicker` —
 * and the `GET /v1/clinical-study/treatment-date-pool` request it fires on mount — runs ONLY
 * while this component is actually mounted, i.e. ONLY in provisional mode; the parent
 * `TreatmentDateRescheduleSection` conditionally MOUNTS this component rather than conditionally
 * calling the hook, respecting the Rules of Hooks. `onSubmit` delegates the actual POST to the
 * parent, which owns `apiError`/`onConflict`/`onRescheduled`; it resolves to `true` on success,
 * so this component resets its own form only when the parent's submission actually succeeded.
 */
function ProvisionalRescheduleForm({
  onSubmit,
}: {
  onSubmit: (body: { treatmentDatePoolEntryId: string }) => Promise<boolean>;
}) {
  const { t } = useTranslation('clinical-study');
  const pool = useTreatmentDatePoolPicker();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProvisionalRescheduleFormValues>({
    resolver: zodResolver(provisionalRescheduleSchema),
  });
  // One hook instance per submit control: this child and the parent's enrolled form live in two
  // components, so a single instance cannot be shared across the boundary (Task 8.1).
  const { pending, run } = usePendingWrite();

  const remainingEntries =
    pool.status === 'ready'
      ? pool.entries.filter((entry) => entry.capacity - entry.consumedCount > 0)
      : [];

  return (
    <>
      <p className="text-xs text-muted-foreground">{t('reschedule.provisionalModeHint')}</p>
      <form
        className="space-y-4"
        onSubmit={(event) =>
          void handleSubmit((values) =>
            run(async () => {
              const succeeded = await onSubmit({
                treatmentDatePoolEntryId: values.treatmentDatePoolEntryId,
              });
              if (succeeded) {
                reset();
              }
            }),
          )(event)
        }
      >
        {pool.status === 'loading' && (
          <p className="text-sm text-muted-foreground">{t('reservation.poolLoading')}</p>
        )}
        {/* SL-0045 Activity 9: 'forbidden' shares the refusal presentation. The state stays in the
              union because a 403 remains reachable for any role holding neither pool code, and it
              must surface as a refusal — never as the raw-ID field this activity deleted. */}
        {(pool.status === 'error' || pool.status === 'forbidden') && (
          <div className="space-y-2">
            <FormFeedback tone="error">{t('reservation.poolLoadError')}</FormFeedback>
            <Button type="button" variant="outline" size="sm" onClick={pool.refetch}>
              {t('reservation.poolRetry')}
            </Button>
          </div>
        )}
        {pool.status === 'ready' && (
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="reschedule-pool-entry">
              {t('reschedule.poolEntryLabel')}
            </label>
            <Controller
              control={control}
              name="treatmentDatePoolEntryId"
              render={({ field }) => (
                <Select value={field.value ?? ''} onValueChange={field.onChange}>
                  <SelectTrigger id="reschedule-pool-entry">
                    <SelectValue placeholder={t('reschedule.poolEntryPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {remainingEntries.map((entry) => (
                      <SelectItem key={entry.id} value={entry.id}>
                        {formatDate(entry.date)} —{' '}
                        {t('reservation.remainingSlots', {
                          count: entry.capacity - entry.consumedCount,
                        })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {remainingEntries.length === 0 && (
              <p className="text-sm text-muted-foreground">{t('reschedule.poolEmptyMessage')}</p>
            )}
          </div>
        )}
        {errors.treatmentDatePoolEntryId && (
          <FormFeedback tone="error" id="reschedule-pool-entry-id-error">
            {errors.treatmentDatePoolEntryId.message}
          </FormFeedback>
        )}
        {/* The `pending` term is SL-0046's in-flight submit guard. Dropping it re-admits the
            double-submit defect the 2026-08-04 incident measured at 1.3 ms and 65 ms apart;
            SL-0045 Activity 9 reduced the pool test to `!== 'ready'` when the
            'forbidden' fallback was deleted, and carried the term through. */}
        <Button type="submit" disabled={pool.status !== 'ready' || pending}>
          {t('reschedule.submit')}
        </Button>
      </form>
    </>
  );
}

/**
 * The reschedule mode is DERIVED from `lifecycleState` (a prop sourced from `SubjectWorkspacePanel`'s
 * own `schedule.lifecycleState`), never user-chosen (Resolution R7; there is no reschedule-mode
 * enum, mirroring the server's own dual-mode design). On any conflict, `onRescheduled` is NOT
 * called, `apiError` renders as a persistent banner ABOVE the form (Resolution R3), and `onConflict`
 * IS called — `SubjectWorkspacePanel` (Task 4.3) wires it to a BACKGROUND schedule refresh
 * (`refreshSchedule({ showLoading: false })`, Task 2.2) that never flips the panel's top-level
 * state to `'loading'`, so this banner (and every sibling section's own inline/persistent error
 * state) survives the refresh unchanged (corrected in the 2026-07-27 draft review — see
 * Divergences; the earlier draft claimed this refresh already happened but never wired it).
 *
 * Post-execution hardening (2026-07-27, WCAG 2.2 SC 4.1.3, Finding 4): the persistent `apiError`
 * banner (both here and in the enrolled-mode form's own field-validation message below) now renders
 * through `FormFeedback` (`role="alert"`) — a survives-background-refresh mount, not a toggled
 * `aria-live` region, per that same component's own doc comment; `reschedule-actual-treatment-date`
 * carries `aria-invalid`/`aria-describedby` linking it to that message when present.
 */
export function TreatmentDateRescheduleSection({
  studyId,
  subjectId,
  lifecycleState,
  onRescheduled,
  onConflict,
}: {
  studyId: string;
  subjectId: string;
  lifecycleState: 'PROVISIONAL' | 'ENROLLED';
  onRescheduled: (subject: StudySubject) => void;
  onConflict?: () => void;
}) {
  const { t } = useTranslation('clinical-study');
  const [apiError, setApiError] = useState<string | null>(null);

  const enrolledForm = useForm<EnrolledRescheduleForm>({
    resolver: zodResolver(enrolledRescheduleSchema),
  });
  const { pending, run } = usePendingWrite();

  const submitReschedule = async (
    body: { treatmentDatePoolEntryId: string } | { actualTreatmentDate: string },
  ): Promise<boolean> => {
    setApiError(null);
    const { data, error, response } = await apiClient.POST(
      '/v1/clinical-study/studies/{studyId}/subjects/{subjectId}/treatment-date-reschedule',
      { params: { path: { studyId, subjectId } }, body },
    );
    if (error !== undefined || !response.ok || data === undefined) {
      setApiError(error?.detail ?? t('reschedule.genericError'));
      onConflict?.();
      return false;
    }
    onRescheduled(data);
    toast.success(t('reschedule.rescheduledToast'));
    return true;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('reschedule.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {apiError && <FormFeedback tone="error">{apiError}</FormFeedback>}
        {lifecycleState === 'PROVISIONAL' ? (
          <ProvisionalRescheduleForm onSubmit={submitReschedule} />
        ) : (
          <>
            <p className="text-xs text-muted-foreground">{t('reschedule.enrolledModeHint')}</p>
            <form
              className="space-y-4"
              onSubmit={(event) =>
                void enrolledForm.handleSubmit((values) =>
                  run(async () => {
                    const succeeded = await submitReschedule({
                      actualTreatmentDate: values.actualTreatmentDate,
                    });
                    if (succeeded) {
                      enrolledForm.reset();
                    }
                  }),
                )(event)
              }
            >
              <div className="space-y-1">
                <label className="text-sm font-medium" htmlFor="reschedule-actual-treatment-date">
                  {t('reschedule.actualTreatmentDateLabel')}
                </label>
                <Input
                  id="reschedule-actual-treatment-date"
                  type="date"
                  aria-invalid={
                    enrolledForm.formState.errors.actualTreatmentDate ? true : undefined
                  }
                  aria-describedby={
                    enrolledForm.formState.errors.actualTreatmentDate
                      ? 'reschedule-actual-treatment-date-error'
                      : undefined
                  }
                  {...enrolledForm.register('actualTreatmentDate')}
                />
                {enrolledForm.formState.errors.actualTreatmentDate && (
                  <FormFeedback tone="error" id="reschedule-actual-treatment-date-error">
                    {enrolledForm.formState.errors.actualTreatmentDate.message}
                  </FormFeedback>
                )}
              </div>
              <Button type="submit" disabled={pending}>
                {t('reschedule.submit')}
              </Button>
            </form>
          </>
        )}
      </CardContent>
    </Card>
  );
}
